import type { CalendarProviderId } from "./types";
import type { MeetingReportTab } from "./types/meetingReportTypes";
import { withQueryParamsUrl } from "./utils";

const CHROME_EXTENSION_ROUTES = {
  REDIRECT: "/chrome-extension-redirect",
};

const GO_ROUTES = {
  DOWNLOAD_TRANSCRIPT: "/go/downloadTranscript",
  DOWNLOAD_SUMMARY: "/go/downloadSummary",
  SAMPLE_REPORT: "/go/sampleReport",
  SLACK_USER_CONNECT: "/go/connectUserSlack",
  ZOOM_HOME: "/go/zoomHome",
};

const EMBEDDED_ROUTES = {
  EMBEDDED_ROOT: "/embedded",
  MEETING_TOOLS_HOME: "/home",
  MEETING_TOOLS_DASHBOARD: "/dash",

  // Bot routes
  BOT_ROOT: "/bot",

  // Utility routes
  RECORDING_ENDED: "/recording-ended",
  RECORDING_DELETED: "/recording-deleted",
  RECORDING_TIME_LIMIT_REACHED: "/recording-time-limit-reached",

  // Notetaker routes
  NOTETAKER: "/notetaker",

  // Teams routes
  TEAMS_SIGN_UP: "/teams_auth_create",
  TEAMS_SIGN_IN: "/teams_auth_signin",
  TEAMS_IN_APP_SIGN_IN: "/teams_signin",

  // Zoom routes
  ZOOM_REAUTH_REQUIRED: "/reauth-required",
  ZOOM_MEETING: "/zoom/meeting",
  ZOOM_WAIT_FOR_HOST: "/zoom/wait-for-host",
  // zoom onboarding flow
  ZOOM_WELCOME: "/zoom/welcome/account",
  ZOOM_WELCOME_SIGNIN: "/zoom/welcome/signin",
  ZOOM_WELCOME_ACCOUNT_REQUIRED: "/zoom/welcome/account_required",
  ZOOM_WELCOME_SETTINGS: "/zoom/welcome/settings",
  ZOOM_WELCOME_WHATS_NEXT: "/zoom/welcome/whats-next",
};

// routes that are no longer supported, but we need knowledge of them for redirecting to the new route
const LEGACY_ROUTES = {
  INTEGRATIONS_CONFLUENCE: "/analytics/integrations/user/storage/confluence",
  INTEGRATIONS_JIRA: "/analytics/integrations/user/workflow/jira",
  INTEGRATIONS_SLACK: "/analytics/integrations/slack",
  INTEGRATIONS_WEBHOOKS: "/analytics/integrations/webhooks",
  INTEGRATIONS_ZAPIER: "/analytics/integrations/zapier",
  USER_SETTINGS_CALENDAR: "/analytics/settings/user/calendar",
  USER_SETTINGS_CONNECTED_APPS: "/analytics/settings/user/calendar",
  USER_SETTINGS_RECORDING_AND_PLAYBACK:
    "/analytics/settings/user/recordingAndPlayback",
  USER_SETTINGS_SHARING: "/analytics/settings/user/sharing",
  USER_SETTINGS_CUSTOM_VOCABULARY: "/analytics/settings/user/customVocabulary",
  USER_SETTINGS_SMART_SCHEDULER: "/analytics/settings/user/smartScheduler",
  USER_SETTINGS_OTHER: "/analytics/settings/user/other",
};

// Only meant to be referenced by other routes
// Prefixes do not guarantee a navigable route
const ROUTE_PREFIXES = {
  WORKSPACE_ROOT: "/analytics/workspace",
};

export const USER_SETTING_SECTION_IDS = {
  ACCOUNT: "account",
  ACTIVE_SESSIONS: "active-sessions",
  ASSISTANT_DISPLAY: "assistant-display",
  AUTO_JOIN_PREFERENCES: "auto-join-preferences",
  AUTO_TAGS: "auto-tags",
  CALENDAR: "calendar",
  CALENDAR_AVAILABILITY: "calendar-availability",
  COLLABORATION: "collaboration",
  CONTACT_GROUPS: "contact-groups",
  CONTACT_PREFERENCES: "contact-preferences",
  CRM: "crm",
  CUSTOM_TAGS: "custom-tags",
  CUSTOM_VOCABULARY: "custom-vocabulary",
  CUSTOMER_EXPERIENCE: "customer-experience",
  DELETE_ACCOUNT: "delete-account",
  EMAIL_SUBSCRIPTION: "email-subscription",
  GENERAL: "general",
  MESSAGING: "messaging",
  NOTE_TAKING: "note-taking",
  NOTIFICATIONS: "notifications",
  SHARING_PREFERENCES: "sharing-preferences",
  PLAYBACK: "playback",
  PRODUCTIVITY: "productivity",
  REPORT_DISTRIBUTION: "report-distribution",
  SMART_SCHEDULER_LINKS: "smart-scheduler-links",
  SSO: "sso",
  WORKFLOW: "workflow",
  ZOOM_MEETING_ACCESS: "zoom-meeting-access",
};

const BASE_ANALYTICS_ROUTES = {
  // utility routes
  SECURE_SIGN_IN: "/analytics/secure_signin",
  SIGN_IN: "/analytics/signin",
  SIGN_UP: "/analytics/signup",
  SIGN_OUT: "/analytics/signout",
  OAUTH_RETURN: "/analytics/oauth_return",
  CONNECT_RETURN: "/analytics/connect-return",
  FORGOT_PASSWORD: "/analytics/forgot-password",
  FORGOT_PASSWORD_SUCCESS: "/analytics/forgot-password/success",
  READ_ADMIN: "/analytics/admin",
  SSO_ADD: "/analytics/sso/add",
  SINGLE_CONNECT_CALENDAR: "/analytics/sso/calendar",
  MEETING_FILE_DOWNLOAD: "/analytics/meeting-file-download",
  TOPIC_FILE_DOWNLOAD: "/analytics/topic-file-download",
  OPT_OUT_ROOT: "/analytics/opt-out",
  NOT_FOUND: "/analytics/404",

  // onboarding flow
  WELCOME: "/analytics/welcome",
  WELCOME_CREATE_ACCOUNT: "/analytics/welcome/createAccount",
  WELCOME_SEARCH_COPILOT_INTRODUCTION:
    "/analytics/welcome/searchCopilotIntroduction",
  WELCOME_SEARCH_COPILOT_INTEGRATIONS:
    "/analytics/welcome/searchCopilotIntegrations",
  WELCOME_WORKSPACE: "/analytics/welcome/workspace",
  WELCOME_CALENDAR: "/analytics/welcome/calendar",
  WELCOME_MEETINGS: "/analytics/welcome/meetings",
  WELCOME_SETTINGS: "/analytics/welcome/settings",
  WELCOME_PLATFORMS: "/analytics/welcome/platforms",
  WELCOME_SHARING: "/analytics/welcome/sharing",
  WELCOME_RECORDING_AND_PLAYBACK: "/analytics/welcome/recordingAndPlayback",
  WELCOME_WRAP_UP: "/analytics/welcome/wrapUp",
  WELCOME_LINK_ZOOM: "/analytics/welcome/linkZoom",
  WELCOME_ADD_RSS: "/analytics/welcome/smartScheduler",

  // new onboarding flow
  WELCOME_INTEGRATIONS: "/analytics/welcome/integrations",
  WELCOME_WRAP_UP_NEW: "/analytics/welcome/wrap-up",

  // application pages
  ADD_READ: "/analytics/add",
  COACHING_ROOT: "/analytics/coaching",
  COACHING_TALKING_PACE: "/analytics/coaching/talking-pace",
  RECOMMENDATIONS: "/analytics/recommendations",
  REVIEWED_RECOMMENDATIONS: "/analytics/recommendations/reviewed",
  REPORTS: "/analytics/reports",
  REPORTS_SEARCH: "/analytics/reports/search",
  SHARED_REPORTS: "/analytics/reports/shared",
  INCOMPLETE_REPORTS: "/analytics/reports/incomplete",
  CALENDAR: "/analytics/calendar",
  CALENDAR_SCHEDULING: "/analytics/calendar/scheduling",
  PERSONAL_MEETING_POLICY: "/analytics/meeting-policy",
  DAILY_READ: "/analytics/daily-read",
  MEETING_REPORT_ROOT: "/analytics/meetings",
  TOPIC_REPORT_ROOT: "/analytics/topics",

  // Search
  SEARCH: "/analytics/search",
  SEARCH_RESULTS: "/analytics/search/results",
  SHARED_SEARCHES: "/analytics/search/shared",

  // Integrations
  INTEGRATIONS_ROOT: "/analytics/integrations",

  INTEGRATIONS_USER_ROOT: "/analytics/integrations/user",
  INTEGRATIONS_GOOGLE_CALENDAR: "/analytics/integrations/user/calendar/google",
  INTEGRATIONS_OUTLOOK_CALENDAR:
    "/analytics/integrations/user/calendar/outlook",
  INTEGRATIONS_ZOOM_CALENDAR: "/analytics/integrations/user/calendar/zoom",
  INTEGRATIONS_GOOGLE_EMAIL: "/analytics/integrations/user/productivity/google",
  INTEGRATIONS_OUTLOOK_EMAIL:
    "/analytics/integrations/user/productivity/outlook",
  INTEGRATIONS_GOOGLE_DOCS: "/analytics/integrations/user/note-taking/google",
  INTEGRATIONS_ONENOTE: "/analytics/integrations/user/note-taking/onenote",
  INTEGRATIONS_TEAMS_INGEST: "/analytics/integrations/user/productivity/teams",
  INTEGRATIONS_SLACK_INGEST: "/analytics/integrations/user/productivity/slack",
  INTEGRATIONS_USER_SLACK: "/analytics/integrations/user/messaging/slack",
  INTEGRATIONS_USER_TEAMS: "/analytics/integrations/user/messaging/teams",
  INTEGRATIONS_CONFLUENCE:
    "/analytics/integrations/user/collaboration/confluence",
  INTEGRATIONS_GOOGLE_DRIVE:
    "/analytics/integrations/user/collaboration/google-drive",
  INTEGRATIONS_JIRA: "/analytics/integrations/user/collaboration/jira",
  INTEGRATIONS_ONEDRIVE: "/analytics/integrations/user/collaboration/onedrive",
  INTEGRATIONS_NOTION: "/analytics/integrations/user/collaboration/notion",
  INTEGRATIONS_HUBSPOT: "/analytics/integrations/user/crm/hubspot",
  INTEGRATIONS_SALESFORCE: "/analytics/integrations/user/crm/salesforce",
  INTEGRATIONS_WEBHOOKS: "/analytics/integrations/user/workflow/webhooks",
  INTEGRATIONS_ZAPIER: "/analytics/integrations/user/workflow/zapier",

  INTEGRATIONS_WORKSPACE_ROOT: "/analytics/integrations/workspace",
  INTEGRATIONS_WORKSPACE_SLACK: "/analytics/integrations/workspace/slack",
  INTEGRATIONS_WORKSPACE_TEAMS: "/analytics/integrations/workspace/teams",

  // "For You" pages
  FOR_YOU: "/analytics/for-you",
  FOR_YOU_TAGS: "/analytics/for-you/tags",
  FOR_YOU_WORKSPACE: "/analytics/for-you/workspace",
  FOR_YOU_TEAMS: "/analytics/for-you/teams",

  // Home Pages
  NEWSFEED: "/analytics/home",

  // settings
  SETTINGS_ROOT: "/analytics/settings",

  WORKSPACE_SETTINGS_ROOT: "/analytics/settings/workspace",
  NEW_WORKSPACE: "/analytics/settings/workspace/new",
  JOIN_WORKSPACE: "/analytics/settings/workspace/join",
  INVITATION_WORKSPACE: "/analytics/settings/workspace/invitation",
  TRANSFER_WORKSPACE: "/analytics/settings/workspace/transfer",
  DELETE_WORKSPACE: "/analytics/settings/workspace/delete",

  USER_SETTINGS_ROOT: "/analytics/settings/user",
  USER_SETTINGS_PROFILE: "/analytics/settings/user/profile",
  USER_SETTINGS_INTEGRATIONS: "/analytics/settings/user/integrations",
  USER_SETTINGS_ASSISTANT: "/analytics/settings/user/assistant",
  USER_SETTINGS_MEETING_INSIGHTS: "/analytics/settings/user/meeting-insights",
  USER_SETTINGS_REPORTING: "/analytics/settings/user/reporting",
  USER_SETTINGS_SMART_SCHEDULER: "/analytics/settings/user/smart-scheduler",
  USER_SETTINGS_TAGGING: "/analytics/settings/user/tagging",
  USER_SETTINGS_CONTACTS_AND_GROUPS:
    "/analytics/settings/user/contacts-and-groups",
  USER_SETTINGS_CUSTOM_VOCABULARY: "/analytics/settings/user/custom-vocabulary",
  USER_SETTINGS_EMAIL: "/analytics/settings/user/email",
  USER_SETTINGS_ADVANCED: "/analytics/settings/user/advanced",

  // billing
  BILLING: "/analytics/settings/billing",
  BILLING_CHECKOUT_ROOT: "/analytics/settings/billing/checkout",
  BILLING_CHECKOUT_SUBSCRIPTION:
    "/analytics/settings/billing/checkout/subscription",
  BILLING_CHECKOUT_CREDITS: "/analytics/settings/billing/checkout/credits",
  BILLING_CONFIRM_PAYMENT: "/analytics/settings/billing/checkout/confirm",
  BILLING_COMPARE_PLANS: "/analytics/settings/billing/compare-plans",
  BILLING_UPDATE: "/analytics/settings/billing/update",
};

const USER_SETTING_SECTION_ROUTES = {
  USER_SETTINGS_JOIN_PREFERENCES: withQueryParamsUrl(
    BASE_ANALYTICS_ROUTES.USER_SETTINGS_ASSISTANT,
    { id: USER_SETTING_SECTION_IDS.AUTO_JOIN_PREFERENCES },
  ),
  USER_SETTINGS_SHARING_PREFERENCES: withQueryParamsUrl(
    BASE_ANALYTICS_ROUTES.USER_SETTINGS_REPORTING,
    { id: USER_SETTING_SECTION_IDS.SHARING_PREFERENCES },
  ),
  USER_SETTINGS_CONTACT_SYNC: withQueryParamsUrl(
    BASE_ANALYTICS_ROUTES.USER_SETTINGS_CONTACTS_AND_GROUPS,
    { id: USER_SETTING_SECTION_IDS.CONTACT_PREFERENCES },
  ),
};

const ANALYTICS_ROUTES = {
  ...BASE_ANALYTICS_ROUTES,
  ...USER_SETTING_SECTION_ROUTES,
};

const ALIASES = {
  SEARCH_PAGE: ANALYTICS_ROUTES.SEARCH,
  LANDING_PAGE: ANALYTICS_ROUTES.REPORTS,
  COACHING_DEFAULT_SECTION: ANALYTICS_ROUTES.COACHING_TALKING_PACE,
  INTEGRATIONS_DEFAULT_SECTION: ANALYTICS_ROUTES.INTEGRATIONS_GOOGLE_CALENDAR,
  CALENDAR_UPCOMING: ANALYTICS_ROUTES.CALENDAR,
  CONNECT_SLACK_INTEGRATION: ANALYTICS_ROUTES.INTEGRATIONS_USER_SLACK,
  SETTINGS_DEFAULT_SECTION: ANALYTICS_ROUTES.USER_SETTINGS_PROFILE,
  UPGRADE_ACCOUNT: ANALYTICS_ROUTES.BILLING_COMPARE_PLANS,
  USER_SETTINGS_DEFAULT_SECTION: ANALYTICS_ROUTES.USER_SETTINGS_PROFILE,

  MEETING_REPORTS: ANALYTICS_ROUTES.REPORTS,
  MEETING_REPORTS_SEARCH: ANALYTICS_ROUTES.REPORTS_SEARCH,
  SHARED_MEETING_REPORTS: ANALYTICS_ROUTES.SHARED_REPORTS,
  INCOMPLETE_MEETING_REPORTS: ANALYTICS_ROUTES.INCOMPLETE_REPORTS,
};

const SLUGS = {
  MEETING_POLICY: "meeting-policy",
};

const getWorkspaceRootById = (workspaceId: string) =>
  `${ROUTE_PREFIXES.WORKSPACE_ROOT}/${workspaceId}`;
const getWorkspaceSettingsRootById = (workspaceId: string) =>
  `${ANALYTICS_ROUTES.WORKSPACE_SETTINGS_ROOT}/${workspaceId}`;

const PARAMETERIZED_ROUTES = {
  /* Meeting Reports */
  meetingReportById: (meetingId: string) =>
    `${ANALYTICS_ROUTES.MEETING_REPORT_ROOT}/${meetingId}`,
  meetingReportTabById: (meetingId: string, tab: MeetingReportTab) =>
    `${ANALYTICS_ROUTES.MEETING_REPORT_ROOT}/${meetingId}${
      tab === "notes" ? "" : `/${tab}`
    }`,
  meetingReportAtTimestamp: (meetingId: string, timestamp: number) =>
    `${ANALYTICS_ROUTES.MEETING_REPORT_ROOT}/${meetingId}?t=${timestamp}`,

  /* Topic reports */
  topicReportById: (topicId: string, userId: string) =>
    `${ANALYTICS_ROUTES.TOPIC_REPORT_ROOT}/${topicId}?iid=${userId}`,

  /* Workspace */
  workspaceOverviewById: (workspaceId: string) =>
    `${getWorkspaceRootById(workspaceId)}/overview`,
  workspaceMeetingPolicyById: (workspaceId: string) =>
    `${getWorkspaceRootById(workspaceId)}/${SLUGS.MEETING_POLICY}`,
  workspaceSettingsRootById: getWorkspaceSettingsRootById,
  workspaceSettingsById: (workspaceId: string) =>
    `${getWorkspaceSettingsRootById(workspaceId)}/teams`, // teams is the default tab
  workspaceSettingsPeopleById: (workspaceId: string) =>
    `${getWorkspaceSettingsRootById(workspaceId)}/people`,
  workspaceSettingsPermissionsById: (workspaceId: string) =>
    `${getWorkspaceSettingsRootById(workspaceId)}/permissions`,
  workspaceSettingsSettingsById: (
    workspaceId: string, // has a funny name because the route above took it
  ) => `${getWorkspaceSettingsRootById(workspaceId)}/settings`,
  workspaceTeamById: (workspaceId: string, teamId: string) =>
    `${getWorkspaceSettingsRootById(workspaceId)}/teams/${teamId}`,

  /* Recommendations */
  recommendationById: (recommendationId: string) =>
    `${ANALYTICS_ROUTES.RECOMMENDATIONS}?id=${recommendationId}`,

  /* Dashboard routes */
  meetingToolsById: (sessionId: string) =>
    `${EMBEDDED_ROUTES.MEETING_TOOLS_DASHBOARD}/${sessionId}`,
  meetingToolsParticipantsById: (sessionId: string) =>
    `${EMBEDDED_ROUTES.MEETING_TOOLS_DASHBOARD}/${sessionId}/participants`,
  meetingToolsRecapsById: (sessionId: string) =>
    `${EMBEDDED_ROUTES.MEETING_TOOLS_DASHBOARD}/${sessionId}/recaps`,
  meetingToolsMeetingRecapById: (currentSessionId: string, meetingId: string) =>
    `${EMBEDDED_ROUTES.MEETING_TOOLS_DASHBOARD}/${currentSessionId}/recaps/${meetingId}`,
  recordingPromptById: (sessionId: string) => `/${sessionId}/recording-prompt`,
  meetingToolsNotetakerById: (sessionId: string) =>
    `${EMBEDDED_ROUTES.NOTETAKER}/${sessionId}`,

  /* Search routes */
  searchResultsByQuery: (query: string, queryFilters?: string) =>
    `${ANALYTICS_ROUTES.SEARCH_RESULTS}?q=${encodeURIComponent(query)}${queryFilters ? queryFilters : ""}`,

  sharedSearchResultById: (searchId: string) =>
    `${ANALYTICS_ROUTES.SHARED_SEARCHES}/${searchId}`,

  /* Utility routes */
  connectCalendarByProviderId: (providerId: CalendarProviderId) =>
    `${ANALYTICS_ROUTES.SINGLE_CONNECT_CALENDAR}/${providerId}`,
  dailyReadById: (id: string) => `${ANALYTICS_ROUTES.DAILY_READ}/${id}`,
  meetingFileDownloadById: (
    meetingId: string,
    reportType: "transcript" | "summary",
  ) => `${ANALYTICS_ROUTES.MEETING_FILE_DOWNLOAD}/${meetingId}/${reportType}`,
  topicFileDownloadById: (topicId: string) =>
    `${ANALYTICS_ROUTES.TOPIC_FILE_DOWNLOAD}/${topicId}`,
  optOutById: (sessionId: string) =>
    `${ANALYTICS_ROUTES.OPT_OUT_ROOT}/${sessionId}`,
};

// should NOT include external routes -- those belong in links.ts
export default {
  CHROME_EXTENSION: CHROME_EXTENSION_ROUTES,
  GO: GO_ROUTES,
  LEGACY: LEGACY_ROUTES,
  ANALYTICS: ANALYTICS_ROUTES,
  EMBEDDED: EMBEDDED_ROUTES,
  ...ALIASES,

  // spread all for backwards compatibility
  ...EMBEDDED_ROUTES,
  ...ANALYTICS_ROUTES,
  ...PARAMETERIZED_ROUTES,

  SLUGS,
  PREFIXES: ROUTE_PREFIXES,
};
